import {
  AdgroupFormData,
  CampaignFormData,
  SavedAudienceSupportedKeys,
  AreaStatus,
  TargetingKeywordsSearchStatus,
  BEHAVIOR_TARGETING_SCENES_TYPE,
  CreateMode,
  DEFAULT_SAVED_AUDIENCE_ID,
  DefaultActionDaysV2,
  DefaultVideoActionsV2,
  InterestBehaviorScene,
} from '@tt4b/ttam-enums';
import { computed, Ref, ref } from '@vue/composition-api';
import {
  useStore,
  NetStateEnum,
  StateEnum,
  useHooksCache,
  useAdgroupMode,
  useStdTargetingIs,
  useValidateU18,
  useSharedAreaStore,
  useStdObjectiveType,
} from '@tt4b/ttam-hooks';
import { queryToJson, transformLocationsToCountryIds } from '@tt4b/ttam-utils';
import { pick, isEqual, cloneDeep, isObject } from 'lodash-es';
import { Message } from 'byted-vui';
import { i18n } from './../../../i18n';
enum BACKFILL_STATUS {
  SUCCESS = 0,
  FAILED = 1,
  PARTIAL = 2,
}

export function usePropsSavedAudienceVisible(options: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  createMode: Ref<CreateMode>;
}) {
  const { adgroupData, campaignData, createMode } = options;
  const { isSupportSavedAudience } = useStdTargetingIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isEdit } = useAdgroupMode({ createMode });
  const isSavedAudienceVisible = computed(
    () => isSupportSavedAudience.value && !(isEdit.value && !adgroupData.value.savedAudienceId),
  );
  return {
    isSavedAudienceVisible,
  };
}

export function useDataSource() {
  const { getStore } = useStore();
  const { run: loadData } = getStore(NetStateEnum.savedAudienceList, {} as any, false, false);

  return {
    loadData,
  };
}

export function useViewSavedAudienceDetail() {
  const { aadvid } = queryToJson();

  function viewSavedAudienceDetail(item) {
    const link = `${location.origin}/i18n/dmp/adver/detail?cid=${item.value}&aadvid=${aadvid}`;
    window.open(link, '_blank');
  }

  return {
    viewSavedAudienceDetail,
  };
}

export function useSavedAudienceOptions(options?: { campaignFormData; adgroupFormData }) {
  const { state } = useStore();
  const { getAreaMapInArea } = useSharedAreaStore();
  const { campaignFormData, adgroupFormData } = options || {};

  const { isLeadAds } = useStdObjectiveType({ campaignFormData });

  const savedAudienceOptions = computed(() => {
    const list = state[StateEnum.savedAudienceList].data.value || [];
    if (!campaignFormData || !adgroupFormData) {
      return list;
    }
    const areaMap = getAreaMapInArea();
    const res = list.map(item => {
      const countryId = transformLocationsToCountryIds({
        particleLocations: item?.audience?.particleLocations || [],
        areaMap,
      });
      const _adgroupFormData = Object.assign({}, cloneDeep(adgroupFormData.value), item.audience, {
        country: countryId,
      });
      const { isSavedAudienceLimitU18 } = useValidateU18({
        campaignFormData,
        adgroupFormData: ref(_adgroupFormData),
      });
      return {
        ...item,
        disabled: isSavedAudienceLimitU18.value,
      };
    });
    return res;
  });

  const savedAudienceOptionsPopoverContent = computed(() => {
    if (isLeadAds.value) {
      return {
        title: i18n.t('adgroup_U18_limit_saved_audience_tip_title'),
        content: i18n.t('adgroup_U18_limit_saved_audience_tip_desc'),
      };
    }
    return {
      title: i18n.t('adgroup_U18_limit_saved_audience_tip_title'),
      content: i18n.t('adgroup_U18_limit_saved_audience_tip_desc_two'),
    };
  });
  return {
    savedAudienceOptions,
    savedAudienceOptionsPopoverContent,
  };
}

export function useSavedAudienceEffect(options: { adgroupFormData; campaignFormData }) {
  const { adgroupFormData, campaignFormData } = options;
  const { savedAudienceOptions } = useSavedAudienceOptions({
    campaignFormData: ref(campaignFormData),
    adgroupFormData: ref(adgroupFormData),
  });

  function checkIsEqual(template, current) {
    if (Array.isArray(template) && Array.isArray(current)) {
      if (template.length !== current.length) {
        return false;
      }

      for (let i = 0; i < template.length; i++) {
        if (Array.isArray(template[i]) && Array.isArray(current)) {
          const checkStatus = checkIsEqual(template[i], current[i]);
          if (!checkStatus) {
            return false;
          }
        } else if (!current.includes(template[i])) {
          return false;
        }
      }
      return true;
    } else {
      return template === current;
    }
  }

  function handelSavedAudienceEffect() {
    const { savedAudienceId } = adgroupFormData;
    let finaleSavedAudienceId = DEFAULT_SAVED_AUDIENCE_ID;
    if (savedAudienceId) {
      const savedAduienceTemplateFormData = savedAudienceOptions.value.find(option => option.value === savedAudienceId);
      const currentAudienceSetting = pick(adgroupFormData, SavedAudienceSupportedKeys);
      const templateAudienceFormData = savedAduienceTemplateFormData?.audience || {};
      const templateAudienceFormDataKeys = Object.keys(templateAudienceFormData);
      if (templateAudienceFormDataKeys.length) {
        // 扩展新的saved audience字段后，存量广告是否使用saved audience判断不受影响（存量saved audience key仍为未扩展前的）
        const isEqualTemplate = templateAudienceFormDataKeys.every(key =>
          checkIsEqual(templateAudienceFormData[key], currentAudienceSetting[key]),
        );
        if (isEqualTemplate) {
          finaleSavedAudienceId = savedAudienceId;
        }
      }
    }
    return finaleSavedAudienceId;
  }
  return {
    handelSavedAudienceEffect,
  };
}

export function useSavedAudienceBackfill(opts: {
  savedAudienceConfig;
  campaignFormData: Ref<CampaignFormData>;
  adgroupFormData: Ref<AdgroupFormData>;
}) {
  const { savedAudienceConfig, adgroupFormData, campaignFormData } = opts;
  const { savedAudienceOptions } = useSavedAudienceOptions({
    campaignFormData,
    adgroupFormData,
  });

  async function handleBackfill(options: {
    params: {
      backfillKey: string | number | symbol;
      backfillValue: number | string | number[];
      originDataMap: any;
      backfillAudience?: any;
      setBackfillFormData?: (payload: any) => void;
    };
    config: any;
  }) {
    const { params, config } = options;
    if (typeof config.handleBackfill === 'function') {
      return config.handleBackfill(params);
    } else if (typeof config.handleBackfillAsync === 'function') {
      return await config.handleBackfillAsync(params);
    }

    const { backfillKey, backfillValue, originDataMap, setBackfillFormData } = params;
    let isSupportedBackfill = false;
    if (Array.isArray(backfillValue)) {
      isSupportedBackfill = backfillValue.every(val => originDataMap[val]);
    } else {
      isSupportedBackfill = originDataMap[backfillValue] === '' || Boolean(originDataMap[backfillValue]);
    }

    if (isSupportedBackfill) {
      setBackfillFormData({ [backfillKey]: backfillValue });
    }

    return isSupportedBackfill;
  }

  function getBackfillStatus(options: {
    isAllBackfillSuccess: boolean;
    backfillCount: number;
    savedAudienceId: number;
  }) {
    const { isAllBackfillSuccess, backfillCount, savedAudienceId } = options;
    let finaleSavedAudienceId = DEFAULT_SAVED_AUDIENCE_ID;
    let backfillStatus = BACKFILL_STATUS.FAILED;
    if (isAllBackfillSuccess) {
      // 回填成功
      Message.success(`${i18n.t('adgroup_target_saved_audience_creat_success')}`);
      finaleSavedAudienceId = savedAudienceId;
      backfillStatus = BACKFILL_STATUS.SUCCESS;
    } else if (backfillCount === 0) {
      // 全部回填失败
      Message.error(`${i18n.t('adgroup_target_saved_audience_apply_fail')}`);
      backfillStatus = BACKFILL_STATUS.FAILED;
    } else {
      // 部分回填成功
      Message.error(`${i18n.t('adgroup_target_saved_audience_fully_apply_fail')}`);
      backfillStatus = BACKFILL_STATUS.PARTIAL;
    }
    return {
      finaleSavedAudienceId,
      backfillStatus,
    };
  }

  async function handleSavedAudienceBackfill(options: { savedAudienceId: number }) {
    const { savedAudienceId } = options;
    const savedAduienceBackfillFormData = ref(cloneDeep(pick(adgroupFormData.value, SavedAudienceSupportedKeys)));
    const savedAudience = cloneDeep(savedAudienceOptions.value.find(item => item.value === savedAudienceId));
    if (!savedAudience) {
      return {
        finaleSavedAudienceId: DEFAULT_SAVED_AUDIENCE_ID,
        isAllBackfillSuccess: false,
        backfillFormData: savedAduienceBackfillFormData.value,
      };
    }
    const selectedSavedAudience = savedAudience.audience || {};
    const savedAudienceKeys = Object.keys(selectedSavedAudience);
    const protectedField = savedAudienceConfig.value?.protectedField || [];
    let backfillCount = 0;
    const unSupportedBackfill = [];

    function _setBackfillFormData(payload) {
      Object.assign(savedAduienceBackfillFormData.value, payload);
    }

    for (const key of savedAudienceKeys) {
      // const key = savedAudienceKeys[i];

      if (protectedField.includes(key)) {
        ++backfillCount;
        continue;
      }

      const config = savedAudienceConfig.value?.config[key] || {};
      const originDataMap = config.originDataMap || {};

      const backfillValue = selectedSavedAudience[key];

      // 支持直接回填
      if (config.isSupportedDirectBackfill) {
        savedAduienceBackfillFormData.value[key] = backfillValue;
        ++backfillCount;
        continue;
      }

      // 不支持直接回填
      if (config.isSupportedInCurrentScene) {
        // let isSupportedInCurrentScene = false;
        // 当前业务场景支持此定向设置，进一步判断
        const params = {
          backfillKey: key,
          backfillValue,
          originDataMap,
          backfillAudience: selectedSavedAudience,
          setBackfillFormData: _setBackfillFormData,
        };

        const isSupportedInCurrentScene = await handleBackfill({ params, config });

        if (isSupportedInCurrentScene) {
          ++backfillCount;
        } else {
          unSupportedBackfill.push(key);
        }
      } else {
        // saved audience 设置等于默认值，也认为是回填成功的
        if (isEqual(savedAduienceBackfillFormData.value[key], backfillValue)) {
          ++backfillCount;
        } else {
          unSupportedBackfill.push(key);
        }
      }
    }

    const { backfillStatus, finaleSavedAudienceId } = getBackfillStatus({
      isAllBackfillSuccess: savedAudienceKeys.length === backfillCount,
      backfillCount,
      savedAudienceId,
    });

    return {
      backfillStatus,
      backfillFormData: savedAduienceBackfillFormData.value,
      finaleSavedAudienceId,
    };
  }

  return {
    handleSavedAudienceBackfill,
  };
}

export function useAgeBackfill(options: { ageOptionsMap; isLeadAds }) {
  const { ageOptionsMap, isLeadAds } = options;
  const handleAgeBackfill = ({ backfillValue, setBackfillFormData }) => {
    let isSupportBackfill = false;
    if (isLeadAds.value) {
      isSupportBackfill = backfillValue.every(
        value => ageOptionsMap.value[value] && !ageOptionsMap.value[value]?.disableLeadAds,
      );
    } else {
      isSupportBackfill = backfillValue.every(value => ageOptionsMap.value?.[value]);
    }

    if (isSupportBackfill) {
      setBackfillFormData({ age: backfillValue });
    }
    return isSupportBackfill;
  };

  return {
    handleAgeBackfill,
  };
}

export function useParticleLocationsBackfill(options: { areaMap }) {
  const { areaMap } = options;
  const handleParticleLocationsBackfill = ({ backfillValue, setBackfillFormData }) => {
    const isSupportBackfill = backfillValue.every(
      value => areaMap.value?.[value] && areaMap.value[value]?.status === AreaStatus.ACTIVE,
    );
    if (isSupportBackfill) {
      setBackfillFormData({ particleLocations: backfillValue });
    }
    return isSupportBackfill;
  };

  return {
    handleParticleLocationsBackfill,
  };
}

export function useInterestKeywordsBackfill(
  options: Ref<{
    loadInterestKeywordsInfo;
    interestKeywordsInfoMap;
    isFiveInOne;
  }>,
) {
  const handleInterestKeywordsBackfill = async ({ backfillValue, backfillAudience, setBackfillFormData }) => {
    const { loadInterestKeywordsInfo, interestKeywordsInfoMap, isFiveInOne } = options.value;
    await loadInterestKeywordsInfo({
      interestKeywordsI18n: backfillValue,
      interestKeywordsLangI18n: backfillAudience.interestKeywordsLangI18n,
    });

    const isSupportedInCurrentScene = backfillValue.every(value => {
      const keyword = isFiveInOne
        ? interestKeywordsInfoMap.value?.[InterestBehaviorScene.INTEREST]?.[value]
        : interestKeywordsInfoMap.value?.[value];
      return (
        keyword &&
        ![TargetingKeywordsSearchStatus.LEGAL_OFFLINE, TargetingKeywordsSearchStatus.OFFLINE].includes(
          keyword?.keyword_status,
        )
      );
    });

    if (isSupportedInCurrentScene) {
      setBackfillFormData({
        interestKeywordsI18n: backfillValue,
        interestKeywordsLangI18n: backfillAudience.interestKeywordsLangI18n,
      });
    }
    return isSupportedInCurrentScene;
  };

  return {
    handleInterestKeywordsBackfill,
  };
}

export function useBehaviorBackfill(options: Ref<{ hashtagBehaviorMap; loadHashtagsInfo; isFiveInOne }>) {
  const handleBehaviorBackfillAsync = async ({ backfillAudience, setBackfillFormData }) => {
    const { hashtagBehaviorMap, loadHashtagsInfo, isFiveInOne } = options.value;
    const { actionScenesV2, actionCategoriesV2, actionDaysV2, videoActionsV2 } = JSON.parse(
      JSON.stringify(backfillAudience),
    );
    let isSupportBackfillInFiveInOne = true;
    if (isFiveInOne) {
      const scenes = [...actionScenesV2];
      scenes.forEach(scene => {
        const scenesIndex = actionScenesV2.findIndex(item => item === scene);
        if (scenesIndex > -1) {
          const action = videoActionsV2[scenesIndex];
          const days = actionDaysV2[scenesIndex];
          const isEqualDefaultDays = DefaultVideoActionsV2[scene]()
            .map(item => item.value)
            .every(val => action.includes(val));
          if (!isEqualDefaultDays || !isEqual(days, DefaultActionDaysV2[scene].id)) {
            // 无法回填
            actionScenesV2.splice(scenesIndex, 1);
            actionCategoriesV2.splice(scenesIndex, 1);
            actionDaysV2.splice(scenesIndex, 1);
            videoActionsV2.splice(scenesIndex, 1);
            isSupportBackfillInFiveInOne = false;
          }
        }
      });
    }

    if (!actionScenesV2?.includes(BEHAVIOR_TARGETING_SCENES_TYPE.hashtag)) {
      setBackfillFormData({
        actionScenesV2,
        actionCategoriesV2,
        actionDaysV2,
        videoActionsV2,
      });

      return isSupportBackfillInFiveInOne;
    }
    const hashtagIndex = actionScenesV2.findIndex(scene => scene === BEHAVIOR_TARGETING_SCENES_TYPE.hashtag);
    if (hashtagIndex > -1) {
      const backfillValue = actionCategoriesV2[hashtagIndex];
      await loadHashtagsInfo({ categories: backfillValue });
      const isSupportBackfill = backfillValue.every(id => {
        let hashtag = hashtagBehaviorMap.value[id];
        if (isFiveInOne && isObject(hashtagBehaviorMap.value[InterestBehaviorScene.HASHTAG])) {
          hashtag = hashtagBehaviorMap.value[InterestBehaviorScene.HASHTAG][id];
        }
        return (
          hashtag &&
          ![TargetingKeywordsSearchStatus.OFFLINE, TargetingKeywordsSearchStatus.LEGAL_OFFLINE].includes(hashtag.status)
        );
      });
      if (isSupportBackfill) {
        setBackfillFormData({
          actionScenesV2,
          actionCategoriesV2,
          actionDaysV2,
          videoActionsV2,
        });
      } else {
        actionScenesV2.splice(hashtagIndex, 1);
        actionCategoriesV2.splice(hashtagIndex, 1);
        actionDaysV2.splice(hashtagIndex, 1);
        videoActionsV2.splice(hashtagIndex, 1);
        setBackfillFormData({
          actionScenesV2,
          actionCategoriesV2,
          actionDaysV2,
          videoActionsV2,
        });
      }
      return isSupportBackfillInFiveInOne && isSupportBackfill;
    }
    return isSupportBackfillInFiveInOne;
  };

  return { handleBehaviorBackfillAsync };
}

export function useCarriersBackfill() {
  function handleCarriersBackfill({ backfillAudience, setBackfillFormData }) {
    const { particleLocations, carriers } = backfillAudience;
    if (particleLocations.length) {
      setBackfillFormData({ carriers });
      return true;
    } else {
      return false;
    }
  }
  return { handleCarriersBackfill };
}
